import { useLocalization } from "../../../features/localization/index.js";
import DataTableRow_default from "../../../components/data-table/DataTableRow.vue.js";
import { authorizeOauth2, refreshOauth2Token } from "../helpers/oauth.js";
import { resolveDefaultOAuth2RedirectUri } from "../helpers/resolve-default-oauth2-redirect-url.js";
import OAuthScopesInput_default from "./OAuthScopesInput.vue.js";
import RequestAuthDataTableInput_default from "./RequestAuthDataTableInput.vue.js";
import { Fragment, computed, createBlock, createCommentVNode, createElementBlock, createElementVNode, createTextVNode, createVNode, defineComponent, onBeforeUnmount, openBlock, ref, toDisplayString, unref, watch, withCtx } from "vue";
import { ScalarButton } from "@scalar/components/button";
import { getEnvironmentVariables } from "@scalar/workspace-store/request-example";
import { useToasts } from "@scalar/use-toasts";
import { useLoadingState } from "@scalar/components/loading";
import { pkceOptions } from "@scalar/types/entities";
import { isSecretFieldCleared } from "@scalar/workspace-store/helpers/auth-secret-fields";
//#region src/v2/blocks/scalar-auth-selector-block/components/OAuth2.vue?vue&type=script&setup=true&lang.ts
var _hoisted_1 = { class: "flex h-8 items-center justify-end gap-2 border-t" };
var _hoisted_2 = { class: "w-full p-2" };
var _hoisted_3 = {
	class: "bg-b-2 flex flex-col gap-3 rounded-lg border p-3 text-sm",
	role: "status"
};
var _hoisted_4 = { class: "text-c-2 text-xs leading-normal" };
var _hoisted_5 = { class: "bg-b-1 font-code w-fit max-w-full rounded border px-3 py-2 text-base font-medium tracking-widest break-all select-all" };
var _hoisted_6 = ["href"];
var _hoisted_7 = { class: "flex items-center justify-between gap-3" };
var _hoisted_8 = { class: "text-c-2 text-xs" };
var _hoisted_9 = { class: "flex h-8 w-full items-center justify-end border-t" };
var OAuth2_vue_vue_type_script_setup_true_lang_default = /*@__PURE__*/ defineComponent({
	__name: "OAuth2",
	props: {
		environment: {},
		flows: {},
		type: {},
		selectedScopes: {},
		scheme: {},
		server: {},
		proxyUrl: {},
		name: {},
		eventBus: {},
		options: { default: () => ({}) },
		hideActions: { type: Boolean }
	},
	emits: [
		"update:selectedScopes",
		"upsert:scope",
		"delete:scope"
	],
	setup(__props, { emit: __emit }) {
		const emits = __emit;
		const devicePrompt = ref(null);
		let deviceController;
		const cancelDeviceAuthorization = () => {
			deviceController?.abort();
			devicePrompt.value = null;
		};
		onBeforeUnmount(cancelDeviceAuthorization);
		watch(() => [
			__props.type,
			__props.name,
			__props.flows
		], cancelDeviceAuthorization);
		const { translate } = useLocalization();
		const loader = useLoadingState();
		const { toast } = useToasts();
		/** The current OAuth flow based on the selected type */
		const flow = computed(() => __props.flows[__props.type]);
		const selectedScopes = computed(() => {
			const definedScopes = flow.value.scopes;
			if (!definedScopes) return [];
			return __props.selectedScopes.filter((scope) => Object.hasOwn(definedScopes, scope));
		});
		/**
		* Show the client secret whenever the flow supports one. PKCE and a client
		* secret are independent, so confidential clients can use both together
		* (see RFC 9700 Section 2.1.1).
		*/
		const showClientSecret = computed(() => "x-scalar-secret-client-secret" in flow.value);
		const clientSecretValue = computed(() => {
			return flow.value["x-scalar-secret-client-secret"] ?? "";
		});
		/** Updates the security scheme base */
		const handleOauth2Update = (payload) => {
			if (__props.scheme.type === "openIdConnect") return handleOauth2SecretsUpdate(payload);
			if (__props.scheme.type !== "oauth2") return;
			__props.eventBus.emit("auth:update:security-scheme", {
				payload: {
					type: __props.scheme.type,
					flows: { [__props.type]: payload }
				},
				name: __props.name
			});
		};
		/** Updates the flow secrets */
		const handleOauth2SecretsUpdate = (payload) => {
			if (__props.scheme.type !== "oauth2" && __props.scheme.type !== "openIdConnect") return;
			__props.eventBus.emit("auth:update:security-scheme-secrets", {
				payload: {
					type: __props.scheme.type,
					[__props.type]: payload
				},
				name: __props.name
			});
		};
		/** Reset only this field; redirect URI may need its browser default prefilled again. */
		const handleResetSecret = (field) => {
			if (field === "x-scalar-secret-redirect-uri") hasHandledRedirectPrefill.value = false;
			__props.eventBus.emit("auth:reset:security-scheme-secret", {
				name: __props.name,
				flow: __props.type,
				field
			});
		};
		/** Fixed-choice fields must never write an empty string into the document. */
		const handlePkceUpdate = (value) => {
			if (value === "SHA-256" || value === "plain" || value === "no") handleOauth2Update({ "x-usePkce": value });
		};
		/** Clears the flow secrets */
		const clearOauth2Secrets = () => {
			if (loader.isLoading) return;
			__props.eventBus.emit("auth:clear:security-scheme-secrets", { name: __props.name });
		};
		/** Clears stored access and refresh tokens (authorized state) */
		const handleClearAccessTokens = () => {
			if (loader.isLoading) return;
			handleOauth2SecretsUpdate({
				"x-scalar-secret-token": "",
				"x-scalar-secret-refresh-token": ""
			});
		};
		/** Track redirect URI prefill per flow instance to support document switching */
		const prefilledFlowIdentity = ref(null);
		const hasHandledRedirectPrefill = ref(false);
		const resolveFlowIdentity = (currentFlow) => JSON.stringify({
			type: __props.type,
			authorizationUrl: currentFlow && "authorizationUrl" in currentFlow ? currentFlow.authorizationUrl : "",
			tokenUrl: currentFlow && "tokenUrl" in currentFlow ? currentFlow.tokenUrl : "",
			refreshUrl: currentFlow?.refreshUrl ?? "",
			scopes: Object.keys(currentFlow?.scopes ?? {})
		});
		/** Default the redirect-uri to the current origin if we have access to window */
		watch(() => flow.value, (currentFlow) => {
			if (!currentFlow || !("x-scalar-secret-redirect-uri" in currentFlow)) return;
			const flowIdentity = resolveFlowIdentity(currentFlow);
			if (prefilledFlowIdentity.value !== flowIdentity) {
				prefilledFlowIdentity.value = flowIdentity;
				hasHandledRedirectPrefill.value = false;
			}
			if (hasHandledRedirectPrefill.value) return;
			const newRedirectUri = currentFlow["x-scalar-secret-redirect-uri"];
			const defaultRedirectUri = resolveDefaultOAuth2RedirectUri(__props.options);
			hasHandledRedirectPrefill.value = true;
			if (isSecretFieldCleared(currentFlow, "x-scalar-secret-redirect-uri") || newRedirectUri || !defaultRedirectUri || __props.options.captureOAuth2Callback) return;
			handleOauth2SecretsUpdate({ "x-scalar-secret-redirect-uri": defaultRedirectUri });
		}, { immediate: true });
		/**
		* Authorizes the user using the specified OAuth flow.
		* Opens the appropriate OAuth dialog and/or performs the token exchange.
		*/
		const handleAuthorize = async () => {
			if (loader.isLoading) return;
			loader.start();
			const controller = new AbortController();
			deviceController = controller;
			const [error, tokens] = await authorizeOauth2(__props.flows, __props.type, selectedScopes.value, __props.server, __props.proxyUrl, getEnvironmentVariables(__props.environment), __props.options.customFetch, __props.options.captureOAuth2Callback, {
				signal: controller.signal,
				onPrompt: (prompt) => {
					devicePrompt.value = prompt;
				}
			});
			devicePrompt.value = null;
			await loader.clear();
			if (controller.signal.aborted) return;
			if (tokens?.accessToken) handleOauth2SecretsUpdate({
				"x-scalar-secret-token": tokens.accessToken,
				...tokens.refreshToken ? { "x-scalar-secret-refresh-token": tokens.refreshToken } : {}
			});
			else {
				console.error(error);
				toast(error?.message ?? translate("apiClient.oauth2.failedToauthorize"), "error");
			}
		};
		/** Refreshing requires a token issued by the provider, not just a compatible flow. */
		const canRefreshToken = computed(() => __props.type !== "implicit" && Boolean(flow.value["x-scalar-secret-refresh-token"]));
		/**
		* Refresh URL placeholder, shows tokenUrl as hint if refreshUrl is not specified.
		* This helps users understand that tokenUrl will be used as fallback.
		*/
		const refreshUrlPlaceholder = computed(() => {
			if ("tokenUrl" in flow.value && flow.value.tokenUrl) return flow.value.tokenUrl;
			return "https://galaxy.scalar.com/oauth/refresh";
		});
		/**
		* Uses the stored refresh token to obtain a new access token
		* via grant_type=refresh_token.
		*/
		const handleRefresh = async () => {
			if (loader.isLoading || __props.type === "implicit" || !canRefreshToken.value) return;
			loader.start();
			const [error, tokens] = await refreshOauth2Token(__props.flows, __props.type, __props.proxyUrl, __props.server, getEnvironmentVariables(__props.environment), __props.options.customFetch);
			await loader.clear();
			if (tokens?.accessToken) handleOauth2SecretsUpdate({
				"x-scalar-secret-token": tokens.accessToken,
				...tokens.refreshToken ? { "x-scalar-secret-refresh-token": tokens.refreshToken } : {}
			});
			else {
				console.error(error);
				toast(error?.message ?? translate("apiClient.oauth2.failedToRefreshToken"), "error");
			}
		};
		/** Updates the secret location */
		const handleSecretLocationUpdate = (value) => {
			if (value !== "body" && value !== "header") return;
			const credentialsLocation = value;
			if (__props.scheme.type !== "openIdConnect") handleOauth2Update({ "x-scalar-credentials-location": credentialsLocation });
			handleOauth2SecretsUpdate({ "x-scalar-credentials-location": credentialsLocation });
		};
		return (_ctx, _cache) => {
			return Boolean(flow.value["x-scalar-secret-token"]) || unref(isSecretFieldCleared)(flow.value, "x-scalar-secret-token") ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
				createVNode(unref(DataTableRow_default), null, {
					default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
						canReset: "",
						class: "border-r-transparent",
						environment: __props.environment,
						modelValue: flow.value["x-scalar-secret-token"],
						placeholder: "QUxMIFlPVVIgQkFTRSBBUkUgQkVMT05HIFRPIFVT",
						type: "password",
						onReset: _cache[0] || (_cache[0] = ($event) => handleResetSecret("x-scalar-secret-token")),
						"onUpdate:modelValue": _cache[1] || (_cache[1] = (v) => handleOauth2SecretsUpdate({ "x-scalar-secret-token": v }))
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.accessToken")), 1)]),
						_: 1
					}, 8, ["environment", "modelValue"])]),
					_: 1
				}),
				canRefreshToken.value ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 0 }, {
					default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
						class: "border-r-transparent",
						environment: __props.environment,
						modelValue: flow.value.refreshUrl ?? "",
						placeholder: refreshUrlPlaceholder.value,
						"onUpdate:modelValue": _cache[2] || (_cache[2] = (v) => handleOauth2Update({ refreshUrl: v }))
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.refreshUrl")), 1)]),
						_: 1
					}, 8, [
						"environment",
						"modelValue",
						"placeholder"
					])]),
					_: 1
				})) : createCommentVNode("", true),
				!__props.hideActions ? (openBlock(), createBlock(unref(DataTableRow_default), {
					key: 1,
					class: "min-w-full"
				}, {
					default: withCtx(() => [createElementVNode("div", _hoisted_1, [canRefreshToken.value ? (openBlock(), createBlock(unref(ScalarButton), {
						key: 0,
						class: "p-0 px-2 py-0.5",
						loader: unref(loader),
						size: "sm",
						variant: "outlined",
						onClick: handleRefresh
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.refresh")), 1)]),
						_: 1
					}, 8, ["loader"])) : createCommentVNode("", true), createVNode(unref(ScalarButton), {
						class: "mr-1 p-0 px-2 py-0.5",
						disabled: unref(loader).isLoading,
						size: "sm",
						variant: "outlined",
						onClick: handleClearAccessTokens
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.clearTokens")), 1)]),
						_: 1
					}, 8, ["disabled"])])]),
					_: 1
				})) : createCommentVNode("", true)
			], 64)) : (openBlock(), createElementBlock(Fragment, { key: 1 }, [
				"deviceAuthorizationUrl" in flow.value ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 0 }, {
					default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
						environment: __props.environment,
						modelValue: flow.value.deviceAuthorizationUrl,
						"onUpdate:modelValue": _cache[3] || (_cache[3] = (v) => handleOauth2Update({ deviceAuthorizationUrl: v }))
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.deviceAuthorizationUrl")), 1)]),
						_: 1
					}, 8, ["environment", "modelValue"])]),
					_: 1
				})) : createCommentVNode("", true),
				devicePrompt.value ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 1 }, {
					default: withCtx(() => [createElementVNode("div", _hoisted_2, [createElementVNode("div", _hoisted_3, [
						createElementVNode("p", _hoisted_4, toDisplayString(unref(translate)("apiClient.oauth2.deviceVerificationPrompt")), 1),
						createElementVNode("strong", _hoisted_5, toDisplayString(devicePrompt.value.userCode), 1),
						createElementVNode("a", {
							class: "text-c-accent w-fit max-w-full text-xs break-all underline underline-offset-2",
							href: devicePrompt.value.verificationUriComplete || devicePrompt.value.verificationUri,
							rel: "noopener noreferrer",
							target: "_blank"
						}, toDisplayString(devicePrompt.value.verificationUri), 9, _hoisted_6),
						createElementVNode("div", _hoisted_7, [createElementVNode("p", _hoisted_8, toDisplayString(unref(translate)("apiClient.oauth2.waitingForAuthorization")), 1), createVNode(unref(ScalarButton), {
							size: "xs",
							variant: "outlined",
							onClick: cancelDeviceAuthorization
						}, {
							default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.cancelDeviceAuthorization")), 1)]),
							_: 1
						})])
					])])]),
					_: 1
				})) : createCommentVNode("", true),
				createVNode(unref(DataTableRow_default), null, {
					default: withCtx(() => ["authorizationUrl" in flow.value ? (openBlock(), createBlock(RequestAuthDataTableInput_default, {
						key: 0,
						canReset: "",
						containerClass: "border-r-0",
						environment: __props.environment,
						modelValue: flow.value["x-scalar-secret-auth-url"] ?? "",
						placeholder: "https://galaxy.scalar.com/authorize",
						onReset: _cache[4] || (_cache[4] = ($event) => handleResetSecret("x-scalar-secret-auth-url")),
						"onUpdate:modelValue": _cache[5] || (_cache[5] = (v) => {
							handleOauth2SecretsUpdate({ "x-scalar-secret-auth-url": v });
							if (v) handleOauth2Update({ authorizationUrl: v });
						})
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.authUrl")), 1)]),
						_: 1
					}, 8, ["environment", "modelValue"])) : createCommentVNode("", true), "tokenUrl" in flow.value ? (openBlock(), createBlock(RequestAuthDataTableInput_default, {
						key: 1,
						canReset: "",
						environment: __props.environment,
						modelValue: flow.value["x-scalar-secret-token-url"] ?? "",
						placeholder: "https://galaxy.scalar.com/token",
						onReset: _cache[6] || (_cache[6] = ($event) => handleResetSecret("x-scalar-secret-token-url")),
						"onUpdate:modelValue": _cache[7] || (_cache[7] = (v) => {
							handleOauth2SecretsUpdate({ "x-scalar-secret-token-url": v });
							if (v) handleOauth2Update({ tokenUrl: v });
						})
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.tokenUrl")), 1)]),
						_: 1
					}, 8, ["environment", "modelValue"])) : createCommentVNode("", true)]),
					_: 1
				}),
				"x-scalar-secret-redirect-uri" in flow.value ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 2 }, {
					default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
						canReset: "",
						environment: __props.environment,
						modelValue: flow.value["x-scalar-secret-redirect-uri"],
						placeholder: __props.options.captureOAuth2Callback ? `${unref(resolveDefaultOAuth2RedirectUri)(__props.options) || "http://127.0.0.1"} (handled automatically)` : unref(translate)("apiClient.oauth2.optionalRedirectUrl"),
						onReset: _cache[8] || (_cache[8] = ($event) => handleResetSecret("x-scalar-secret-redirect-uri")),
						"onUpdate:modelValue": _cache[9] || (_cache[9] = (v) => {
							hasHandledRedirectPrefill.value = true;
							handleOauth2SecretsUpdate({ "x-scalar-secret-redirect-uri": v });
						})
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.redirectUrl")), 1)]),
						_: 1
					}, 8, [
						"environment",
						"modelValue",
						"placeholder"
					])]),
					_: 1
				})) : createCommentVNode("", true),
				"x-scalar-secret-username" in flow.value && "x-scalar-secret-password" in flow.value ? (openBlock(), createElementBlock(Fragment, { key: 3 }, [createVNode(unref(DataTableRow_default), null, {
					default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
						canReset: "",
						class: "text-c-2",
						environment: __props.environment,
						modelValue: flow.value["x-scalar-secret-username"],
						placeholder: "janedoe",
						onReset: _cache[10] || (_cache[10] = ($event) => handleResetSecret("x-scalar-secret-username")),
						"onUpdate:modelValue": _cache[11] || (_cache[11] = (v) => handleOauth2SecretsUpdate({ "x-scalar-secret-username": v }))
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.username")), 1)]),
						_: 1
					}, 8, ["environment", "modelValue"])]),
					_: 1
				}), createVNode(unref(DataTableRow_default), null, {
					default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
						canReset: "",
						environment: __props.environment,
						modelValue: flow.value["x-scalar-secret-password"],
						placeholder: "********",
						type: "password",
						onReset: _cache[12] || (_cache[12] = ($event) => handleResetSecret("x-scalar-secret-password")),
						"onUpdate:modelValue": _cache[13] || (_cache[13] = (v) => handleOauth2SecretsUpdate({ "x-scalar-secret-password": v }))
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.password")), 1)]),
						_: 1
					}, 8, ["environment", "modelValue"])]),
					_: 1
				})], 64)) : createCommentVNode("", true),
				createVNode(unref(DataTableRow_default), null, {
					default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
						canReset: "",
						environment: __props.environment,
						modelValue: flow.value["x-scalar-secret-client-id"],
						placeholder: "12345",
						onReset: _cache[14] || (_cache[14] = ($event) => handleResetSecret("x-scalar-secret-client-id")),
						"onUpdate:modelValue": _cache[15] || (_cache[15] = (v) => handleOauth2SecretsUpdate({ "x-scalar-secret-client-id": v }))
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.clientID")), 1)]),
						_: 1
					}, 8, ["environment", "modelValue"])]),
					_: 1
				}),
				showClientSecret.value ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 4 }, {
					default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
						canReset: "",
						environment: __props.environment,
						modelValue: clientSecretValue.value,
						placeholder: "XYZ123",
						type: "password",
						onReset: _cache[16] || (_cache[16] = ($event) => handleResetSecret("x-scalar-secret-client-secret")),
						"onUpdate:modelValue": _cache[17] || (_cache[17] = (v) => handleOauth2SecretsUpdate({ "x-scalar-secret-client-secret": v }))
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.clientSecret")), 1)]),
						_: 1
					}, 8, ["environment", "modelValue"])]),
					_: 1
				})) : createCommentVNode("", true),
				"x-usePkce" in flow.value ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 5 }, {
					default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
						enum: unref(pkceOptions),
						environment: __props.environment,
						modelValue: flow.value["x-usePkce"],
						readOnly: "",
						"onUpdate:modelValue": handlePkceUpdate
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.usePKCE")), 1)]),
						_: 1
					}, 8, [
						"enum",
						"environment",
						"modelValue"
					])]),
					_: 1
				})) : createCommentVNode("", true),
				__props.type !== "implicit" ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 6 }, {
					default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
						enum: ["header", "body"],
						environment: __props.environment,
						modelValue: flow.value["x-scalar-credentials-location"] || "header",
						placeholder: "header",
						readOnly: "",
						"onUpdate:modelValue": _cache[18] || (_cache[18] = (v) => handleSecretLocationUpdate(v))
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.credentialsLocation")), 1)]),
						_: 1
					}, 8, ["environment", "modelValue"])]),
					_: 1
				})) : createCommentVNode("", true),
				createVNode(unref(DataTableRow_default), null, {
					default: withCtx(() => [createVNode(OAuthScopesInput_default, {
						flow: flow.value,
						flowType: __props.type,
						selectedScopes: selectedScopes.value,
						"onDelete:scope": _cache[19] || (_cache[19] = (v) => emits("delete:scope", v)),
						"onUpdate:selectedScopes": _cache[20] || (_cache[20] = (v) => emits("update:selectedScopes", v)),
						"onUpsert:scope": _cache[21] || (_cache[21] = (v) => emits("upsert:scope", v))
					}, null, 8, [
						"flow",
						"flowType",
						"selectedScopes"
					])]),
					_: 1
				}),
				!__props.hideActions ? (openBlock(), createBlock(unref(DataTableRow_default), {
					key: 7,
					class: "min-w-full"
				}, {
					default: withCtx(() => [createElementVNode("div", _hoisted_9, [__props.scheme.type === "openIdConnect" ? (openBlock(), createBlock(unref(ScalarButton), {
						key: 0,
						class: "mr-1 p-0 px-2 py-0.5",
						disabled: unref(loader).isLoading,
						size: "sm",
						variant: "outlined",
						onClick: clearOauth2Secrets
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.resetDiscovery")), 1)]),
						_: 1
					}, 8, ["disabled"])) : createCommentVNode("", true), createVNode(unref(ScalarButton), {
						class: "mr-0.75 p-0 px-2 py-0.5",
						loader: unref(loader),
						size: "sm",
						variant: "outlined",
						onClick: handleAuthorize
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.oauth2.authorize")), 1)]),
						_: 1
					}, 8, ["loader"])])]),
					_: 1
				})) : createCommentVNode("", true)
			], 64));
		};
	}
});
//#endregion
export { OAuth2_vue_vue_type_script_setup_true_lang_default as default };

//# sourceMappingURL=OAuth2.vue.script.js.map